// src/pages/Dashboard.js
import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { ref, get } from 'firebase/database';
import { db, rtdb } from '../firebase/config';
import { PageHeader, StatCard, Card } from '../components/UI';
import { useAuth } from '../context/AuthContext';

export default function Dashboard() {
  const { admin } = useAuth();
  const [stats, setStats] = useState({ users: 0, staff: 0, staffPending: 0, activeTrains: 0, admins: 0, adminsPending: 0 });
  const [loading, setLoading] = useState(true);
  const [liveTrains, setLiveTrains] = useState([]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      // Firestore counts
      const [staffSnap, pendingStaffSnap, adminsSnap, pendingAdminsSnap] = await Promise.all([
        getDocs(collection(db, 'staff')),
        getDocs(query(collection(db, 'staff'), where('status', '==', 'pending'))),
        getDocs(collection(db, 'admins')),
        getDocs(query(collection(db, 'admins'), where('status', '==', 'pending'))),
      ]);

      // Realtime DB থেকে users count
      const usersRtdbSnap = await get(ref(rtdb, 'users'));
      const usersCount = usersRtdbSnap.exists() ? Object.keys(usersRtdbSnap.val()).length : 0;

      // Realtime DB — active trains today
      const locsSnap = await get(ref(rtdb, 'trains'));
      let activeCount = 0;
      const liveList = [];
      const now = Date.now();

      if (locsSnap.exists()) {
        locsSnap.forEach(trainSnap => {
          const trainId = trainSnap.key;
          const daySnap = trainSnap.child(`locations/${today}`);
          if (daySnap.exists()) {
            let hasActive = false;
            let latestLoc = null;
            daySnap.forEach(staffSnap => {
              const d = staffSnap.val();
              const diff = now - (d.timestamp || 0);
              if (diff < 15 * 60 * 1000 && !d.isOffline) {
                hasActive = true;
                if (!latestLoc || d.timestamp > latestLoc.timestamp) latestLoc = d;
              }
            });
            if (hasActive && latestLoc) {
              activeCount++;
              liveList.push({ trainId, ...latestLoc });
            }
          }
        });
      }

      setStats({
        users: usersCount,
        staff: staffSnap.size,
        staffPending: pendingStaffSnap.size,
        activeTrains: activeCount,
        admins: adminsSnap.size,
        adminsPending: pendingAdminsSnap.size,
      });
      setLiveTrains(liveList.slice(0, 5));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'সুপ্রভাত';
    if (h < 17) return 'শুভ দুপুর';
    return 'শুভ সন্ধ্যা';
  };

  return (
    <div>
      <PageHeader
        title={`${greeting()}, ${admin?.name?.split(' ')[0] || 'Admin'} 👋`}
        sub={`আজকের তারিখ: ${new Date().toLocaleDateString('bn-BD', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`}
      />

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard icon="👥" label="Total Users" value={loading ? '...' : stats.users} color="#2271b1" />
        <StatCard icon="🪪" label="Total Staff" value={loading ? '...' : stats.staff} color="#00a32a" />
        <StatCard icon="⏳" label="Pending Staff" value={loading ? '...' : stats.staffPending} color="#dba617" />
        <StatCard icon="🚂" label="Live Trains" value={loading ? '...' : stats.activeTrains} color="#00a32a" />
        <StatCard icon="🔑" label="Admins" value={loading ? '...' : stats.admins} color="#8b5cf6" />
        <StatCard icon="🔔" label="Pending Admins" value={loading ? '...' : stats.adminsPending} color="#d63638" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Live trains */}
        <Card>
          <h3 style={{ color: '#f0f0f1', margin: '0 0 16px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00a32a', display: 'inline-block', boxShadow: '0 0 6px #00a32a' }}></span>
            এখন চলছে এমন ট্রেন
          </h3>
          {liveTrains.length === 0 ? (
            <p style={{ color: '#646970', fontSize: 13 }}>কোনো live train নেই</p>
          ) : liveTrains.map((t, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #2a2a2a' }}>
              <span style={{ color: '#f0f0f1', fontSize: 13 }}>Train #{t.trainId}</span>
              <span style={{ color: '#00a32a', fontSize: 13, fontFamily: 'DM Mono, monospace' }}>{t.speed?.toFixed(1)} km/h</span>
            </div>
          ))}
        </Card>

        {/* Quick info */}
        <Card>
          <h3 style={{ color: '#f0f0f1', margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>⚡ Quick Links</h3>
          {[
            ['👥 User Management', '/users'],
            ['🪪 Staff Management', '/staff'],
            ['🔑 Admin Management', '/admins'],
            ['🚂 Train Tracker', '/trains'],
          ].map(([label, path]) => (
            <a key={path} href={path} style={{
              display: 'block', padding: '8px 12px', color: '#a7aaad',
              borderRadius: 4, fontSize: 13, transition: 'all .15s',
              borderBottom: '1px solid #2a2a2a',
            }}
              onMouseEnter={e => { e.target.style.background = '#2a2a2a'; e.target.style.color = '#f0f0f1'; }}
              onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = '#a7aaad'; }}>
              {label} →
            </a>
          ))}
        </Card>
      </div>
    </div>
  );
}
